import { useState, useCallback } from "react";
import { MerchantStep } from "./steps/MerchantStep";
import { ModeStep } from "./steps/ModeStep";
import { FileImportStep } from "./steps/FileImportStep";
import { ManualStep } from "./steps/ManualStep";
import { AgreementStep } from "./steps/AgreementStep";
import { AgreementAddDrawer } from "./drawers/AgreementAddDrawer";
import { Stepper } from "./wizard/Stepper";
import { WIZARD_STEPS, wizardActiveIndex } from "./wizard/steps";
import type { ImportMode, ImportStep, Merchant } from "./types";

// Thin orchestrator for the Import wizard. Owns only the navigation state
// (current step, selected merchant, whether a commit result is showing) and
// renders the horizontal Stepper above the active step. All UI/logic lives in
// the per-step components under ./steps; backend contracts are unchanged.
export function ImportScreen() {
  const [step, setStep] = useState<ImportStep>("merchant");
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  // Lifted from FileImportStep / AgreementStep so the stepper can advance to "result".
  const [hasResult, setHasResult] = useState(false);
  // "agreement-manual" mode is a side drawer overlaid on the mode step (not a wizard step), so the
  // stepper stays on "mode" while it is open and closing returns to the mode picker.
  const [agreementDrawerOpen, setAgreementDrawerOpen] = useState(false);

  const handleSelectMerchant = useCallback((m: Merchant) => {
    setMerchant(m);
    setStep("mode");
  }, []);

  const handleSelectMode = useCallback((mode: ImportMode) => {
    // Route each mode to its corresponding wizard step (all map to the "data" step index).
    if (mode === "file") setStep("file");
    else if (mode === "agreements") setStep("agreements");
    else if (mode === "agreement-manual") setAgreementDrawerOpen(true);
    else setStep("manual");
  }, []);

  const handleReset = useCallback(() => {
    setMerchant(null);
    setHasResult(false);
    setAgreementDrawerOpen(false);
    setStep("merchant");
  }, []);

  // Back to the mode picker — also clears any committed-result flag.
  const goToMode = useCallback(() => {
    setHasResult(false);
    setStep("mode");
  }, []);

  const activeIndex = wizardActiveIndex(step, hasResult);

  // Stepper back-navigation — only the merchant(0)/mode(1) nav steps are click targets.
  const handleStepClick = useCallback(
    (index: number) => {
      if (index === 0) handleReset();
      else if (index === 1) goToMode();
    },
    [handleReset, goToMode],
  );

  return (
    <div className="py-2">
      <Stepper
        steps={WIZARD_STEPS}
        activeIndex={activeIndex}
        maxReached={activeIndex}
        onStepClick={handleStepClick}
      />

      {step === "merchant" && <MerchantStep onSelect={handleSelectMerchant} />}

      {step === "mode" && merchant && (
        <ModeStep merchant={merchant} onBack={() => setStep("merchant")} onSelect={handleSelectMode} />
      )}

      {step === "file" && merchant && (
        <FileImportStep
          merchant={merchant}
          onBack={goToMode}
          onReset={handleReset}
          onResultChange={setHasResult}
        />
      )}

      {step === "manual" && merchant && <ManualStep merchant={merchant} onBack={goToMode} />}

      {step === "agreements" && merchant && (
        <AgreementStep
          merchant={merchant}
          onBack={goToMode}
          onReset={handleReset}
          onResultChange={setHasResult}
        />
      )}

      {/* Single-agreement manual add — side drawer overlaid on the mode step (0119). */}
      {agreementDrawerOpen && merchant && (
        <AgreementAddDrawer
          merchant={merchant}
          onClose={() => setAgreementDrawerOpen(false)}
        />
      )}
    </div>
  );
}
